import type { AnyEvent } from '@disdis/shared'
import Dexie, { type Table } from 'dexie'

/**
 * ARCHITECTURE §4.8. Two tables and nothing else: the append only event log, and a short list
 * of device meta keys. CONTRACTS §8 forbids anything else about the child on the device, and
 * `META_KEYS` is what the PRIV-01 test enumerates against.
 */

export type StoredEvent = AnyEvent & { synced: 0 | 1 }

export type MetaRow = { key: string; value: unknown }

/** The complete allow list from ARCHITECTURE §4.8. Prefixed keys are listed by their prefix. */
export const META_KEYS = [
  'activeChildId',
  'deviceId',
  'parentSession',
  'parentGateUntil',
  'windows',
  'modeOverride',
  'featureFlags',
  'audioUnlocked',
  'muted',
] as const
export const META_PREFIXES = ['lastSyncSeq:', 'modeOverride:', 'nightSeen:'] as const

export function isAllowedMetaKey(key: string): boolean {
  if ((META_KEYS as readonly string[]).includes(key)) return true
  return META_PREFIXES.some((prefix) => key.startsWith(prefix) && key.length > prefix.length)
}

export class DisdisDb extends Dexie {
  events!: Table<StoredEvent, string>
  meta!: Table<MetaRow, string>

  constructor(name = 'disdis') {
    super(name)
    this.version(1).stores({
      events: 'id, childId, seq, ts, localDate, synced, [childId+ts]',
      meta: 'key',
    })
  }
}

export const db = new DisdisDb()

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key)
  return row?.value as T | undefined
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  if (!isAllowedMetaKey(key)) {
    throw new Error(`meta key "${key}" is not in the ARCHITECTURE §4.8 allow list`)
  }
  await db.meta.put({ key, value })
}

export async function deleteMeta(key: string): Promise<void> {
  await db.meta.delete(key)
}
